import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import Button from '../ui/button';
import Badge from '../ui/badge';
import {
  X,
  Download,
  RefreshCw,
  BarChart3,
  Clock,
  DollarSign,
  Target,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,

  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { toast } from 'react-hot-toast';
import modelComparisonService from '../../services/modelComparisonService';

const ModelComparisonResults = ({ comparisonId, comparisonName, onClose }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const loadResults = useCallback(async () => {
    try {
      setLoading(true);
      const response = await modelComparisonService.getComparisonResults(comparisonId);
      setResults(response.data);
    } catch (error) {
      console.error('Failed to load results:', error);
      toast.error(error.message || 'Failed to load results');
      // Mock data for development
      setResults({
        comparison_id: comparisonId,
        status: 'completed',
        summary: {
          total_tests: 100,
          completion_time: '5m 32s',
          best_model: 'gpt-4',
          average_accuracy: 87.5
        },
        detailed_results: {
          'openai/gpt-4': {
            accuracy: 92.5,
            average_latency: 1.2,
            total_cost: 0.45,
            quality_score: 89.2,
            test_results: Array.from({ length: 10 }, (_, i) => ({
              test_id: i + 1,
              accuracy: 85 + Math.random() * 15,
              latency: 0.8 + Math.random() * 0.8,
              cost: 0.03 + Math.random() * 0.02
            }))
          },
          'anthropic/claude-3-opus': {
            accuracy: 89.3,
            average_latency: 0.9,
            total_cost: 0.38,
            quality_score: 91.1,
            test_results: Array.from({ length: 10 }, (_, i) => ({
              test_id: i + 1,
              accuracy: 82 + Math.random() * 15,
              latency: 0.6 + Math.random() * 0.6,
              cost: 0.025 + Math.random() * 0.02
            }))
          }
        },
        performance_metrics: {
          accuracy_over_time: Array.from({ length: 10 }, (_, i) => ({
            test: i + 1,
            'openai/gpt-4': 85 + Math.random() * 15,
            'anthropic/claude-3-opus': 82 + Math.random() * 15
          })),
          latency_distribution: [
            { range: '0-0.5s', count: 15 },
            { range: '0.5-1s', count: 35 },
            { range: '1-1.5s', count: 30 },
            { range: '1.5-2s', count: 15 },
            { range: '2s+', count: 5 }
          ]
        },
        completed_at: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  }, [comparisonId]);

  useEffect(() => {
    loadResults();
  }, [comparisonId, loadResults]);

  const handleExport = async (format = 'csv') => {
    try {
      const blob = await modelComparisonService.exportComparison(comparisonId, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `model_comparison_${comparisonId}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Results exported successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to export results');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Loading results...</p>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Results Available</h3>
          <p className="text-gray-600 mb-4">Results could not be loaded for this comparison.</p>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  const chartData = Object.entries(results.detailed_results || {}).map(([model, data]) => ({
    model: model.split('/')[1] || model,
    accuracy: data.accuracy || 0,
    latency: data.average_latency || 0,
    cost: data.total_cost || 0,
    quality: data.quality_score || 0
  }));

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'detailed', label: 'Detailed Results', icon: Target },
    { id: 'performance', label: 'Performance', icon: TrendingUp },
    { id: 'costs', label: 'Cost Analysis', icon: DollarSign }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">{comparisonName} - Results</h2>
            <p className="text-sm text-gray-600">
              Completed on {new Date(results.completed_at).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={() => handleExport('csv')}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex space-x-8 px-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center gap-2 ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2">
                      <Target className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="text-sm text-gray-600">Total Tests</p>
                        <p className="text-xl font-bold">{results.summary?.total_tests || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="text-sm text-gray-600">Completion Time</p>
                        <p className="text-xl font-bold">{results.summary?.completion_time || 'N/A'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="w-5 h-5 text-purple-500" />
                      <div>
                        <p className="text-sm text-gray-600">Best Model</p>
                        <p className="text-xl font-bold">{results.summary?.best_model || 'N/A'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2">
                      <BarChart3 className="w-5 h-5 text-orange-500" />
                      <div>
                        <p className="text-sm text-gray-600">Avg Accuracy</p>
                        <p className="text-xl font-bold">{results.summary?.average_accuracy || 0}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Overall Performance Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Overall Performance Comparison</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    {chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="model" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="accuracy" fill="#3b82f6" name="Accuracy %" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        No data available for comparison
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Radar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Multi-Metric Comparison</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    {chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={chartData}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="model" />
                          <PolarRadiusAxis angle={90} domain={[0, 100]} />
                          <Radar
                            name="Accuracy"
                            dataKey="accuracy"
                            stroke="#3b82f6"
                            fill="#3b82f6"
                            fillOpacity={0.1}
                          />
                          <Radar
                            name="Quality"
                            dataKey="quality"
                            stroke="#10b981"
                            fill="#10b981"
                            fillOpacity={0.1}
                          />
                          <Tooltip />
                        </RadarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        No data available for radar chart
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'detailed' && (
            <div className="space-y-6">
              {Object.entries(results.detailed_results || {}).map(([model, data]) => (
                <Card key={model}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>{model}</span>
                      <div className="flex space-x-2">
                        <Badge variant="outline">
                          Accuracy: {data.accuracy?.toFixed(1)}%
                        </Badge>
                        <Badge variant="outline">
                          Latency: {data.average_latency?.toFixed(2)}s
                        </Badge>
                        <Badge variant="outline">
                          Cost: ${data.total_cost?.toFixed(3)}
                        </Badge>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">
                          {data.accuracy?.toFixed(1)}%
                        </p>
                        <p className="text-sm text-gray-600">Accuracy</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">
                          {data.average_latency?.toFixed(2)}s
                        </p>
                        <p className="text-sm text-gray-600">Avg Latency</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-orange-600">
                          ${data.total_cost?.toFixed(3)}
                        </p>
                        <p className="text-sm text-gray-600">Total Cost</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-purple-600">
                          {data.quality_score?.toFixed(1)}
                        </p>
                        <p className="text-sm text-gray-600">Quality Score</p>
                      </div>
                    </div>

                    {/* Individual Test Results */}
                    {data.test_results && (
                      <div>
                        <h4 className="font-medium mb-3">Individual Test Results</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-2">Test</th>
                                <th className="text-left p-2">Accuracy</th>
                                <th className="text-left p-2">Latency</th>
                                <th className="text-left p-2">Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.test_results.slice(0, 5).map((test, index) => (
                                <tr key={index} className="border-b">
                                  <td className="p-2">Test {test.test_id}</td>
                                  <td className="p-2">{test.accuracy?.toFixed(1)}%</td>
                                  <td className="p-2">{test.latency?.toFixed(2)}s</td>
                                  <td className="p-2">${test.cost?.toFixed(3)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="space-y-6">
              {/* Accuracy Over Time */}
              <Card>
                <CardHeader>
                  <CardTitle>Accuracy Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={results.performance_metrics?.accuracy_over_time || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="test" />
                        <YAxis />
                        <Tooltip />
                        {Object.keys(results.detailed_results || {}).map((model, index) => (
                          <Line
                            key={model}
                            type="monotone"
                            dataKey={model}
                            stroke={COLORS[index % COLORS.length]}
                            strokeWidth={2}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Latency Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Latency Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={results.performance_metrics?.latency_distribution || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="range" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'costs' && (
            <div className="space-y-6">
              {/* Cost Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle>Cost Comparison</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    {chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="model" />
                          <YAxis />
                          <Tooltip formatter={(value) => [`$${value.toFixed(3)}`, 'Cost']} />
                          <Bar dataKey="cost" fill="#f59e0b" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        No data available for cost comparison
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Cost Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Cost Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(results.detailed_results || {}).map(([model, data]) => (
                      <div key={model} className="flex items-center justify-between p-3 border rounded">
                        <span className="font-medium">{model}</span>
                        <div className="text-right">
                          <p className="text-lg font-bold">${data.total_cost?.toFixed(3)}</p>
                          <p className="text-sm text-gray-600">
                            ${(data.total_cost / (results.summary?.total_tests || 1))?.toFixed(4)} per test
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModelComparisonResults;
